import { useTranslation } from "react-i18next";
import { Check as CheckIcon, Inbox as InboxIcon, SearchX as SearchXIcon } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { motion, useReducedMotion } from "../../lib/motion.js";
import { CHANNEL_CARD_ARTWORK } from "../channels/channel-artwork.js";

const CARD_SHADOW = "drop-shadow-[0_10px_16px_rgb(16_58_50/0.16)]";
const MINT_GLOW =
  "pointer-events-none absolute rounded-full bg-[radial-gradient(circle,rgb(85_188_166/0.26)_0%,rgb(85_188_166/0.1)_45%,transparent_70%)]";

const ZERO_CARDS = [
  { id: "instagram", src: CHANNEL_CARD_ARTWORK.instagram, className: "start-2 top-9 size-[72px] -rotate-[9deg]", delay: 0.6 },
  { id: "messenger", src: CHANNEL_CARD_ARTWORK.facebook, className: "end-2 top-9 size-[72px] rotate-[9deg]", delay: 1.2 },
  { id: "whatsapp", src: CHANNEL_CARD_ARTWORK.whatsapp, className: "inset-x-0 top-1 z-[1] mx-auto size-[92px]", delay: 0 },
] as const;

function EmptyStateCopy({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description: string;
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className="flex max-w-[22rem] flex-col items-center gap-5 px-6 text-center"
      data-testid={testId}
    >
      {children}
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 text-[1.0625rem] leading-snug font-semibold tracking-[-0.012em] text-inbox-strong rtl:tracking-normal">
          {title}
        </h2>
        <p className="m-0 text-[0.84375rem] leading-relaxed text-inbox-muted">{description}</p>
      </div>
    </div>
  );
}

/** Thread pane when the organization has no conversations at all. */
export function InboxZeroState(): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  return (
    <EmptyStateCopy
      testId="inbox-zero-thread"
      title={t("inbox.zeroThreadTitle")}
      description={t("inbox.zeroThreadDescription")}
    >
      <div className="relative h-[132px] w-[216px]" aria-hidden="true">
        <span className={`${MINT_GLOW} -inset-8`} />
        {ZERO_CARDS.map((card, index) => (
          <span key={card.id} className={`absolute ${card.className}`}>
            <motion.img
              src={card.src}
              alt=""
              draggable={false}
              className={`size-full select-none ${CARD_SHADOW}`}
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={
                reduceMotion
                  ? { opacity: 1, y: 0 }
                  : { opacity: 1, y: [0, -5, 0] }
              }
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : {
                      opacity: { duration: 0.4, delay: index * 0.08 },
                      y: {
                        duration: 4.8,
                        delay: card.delay,
                        repeat: Infinity,
                        ease: "easeInOut",
                      },
                    }
              }
            />
          </span>
        ))}
      </div>
    </EmptyStateCopy>
  );
}

function MiniRow({ selected, widths }: { selected?: boolean; widths: [string, string] }): React.JSX.Element {
  return (
    <span
      className={
        selected
          ? "relative flex items-center gap-2 rounded-[10px] bg-surface px-2 py-1.5 shadow-inbox-selected ring-1 ring-primary/15"
          : "relative flex items-center gap-2 rounded-[10px] px-2 py-1.5"
      }
    >
      {selected ? (
        <span className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-primary" />
      ) : null}
      <span
        className={
          selected
            ? "size-6 shrink-0 rounded-full bg-[#cdeee0]"
            : "size-6 shrink-0 rounded-full bg-inbox-pill"
        }
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={`h-1.5 rounded-full ${selected ? "bg-[#9fd3bf]" : "bg-[#dfe7e5]"} ${widths[0]}`} />
        <span className={`h-1.5 rounded-full bg-[#ebf0ef] ${widths[1]}`} />
      </span>
      {selected ? (
        <span className="grid size-4 shrink-0 place-items-center rounded-full bg-primary text-white">
          <CheckIcon size={10} strokeWidth={3} />
        </span>
      ) : null}
    </span>
  );
}

/** Thread pane when conversations exist but none is open. */
export function InboxSelectState(): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  return (
    <EmptyStateCopy
      testId="inbox-select-thread"
      title={t("inbox.selectConversationTitle")}
      description={t("inbox.selectConversationDescription")}
    >
      <div className="relative h-[136px] w-[224px]" aria-hidden="true">
        <span className={`${MINT_GLOW} -inset-8`} />
        <motion.span
          className="absolute start-0 top-1 flex w-[150px] flex-col gap-0.5 rounded-[14px] border border-inbox-line bg-inbox-rows p-1.5 shadow-inbox-card"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.4 }}
        >
          <MiniRow widths={["w-3/4", "w-full"]} />
          <MiniRow selected widths={["w-2/3", "w-5/6"]} />
          <MiniRow widths={["w-1/2", "w-4/5"]} />
        </motion.span>
        <motion.span
          className="absolute end-0 bottom-1 flex w-[132px] flex-col gap-1.5 rounded-[14px] border border-inbox-line bg-surface p-2.5 shadow-inbox-float"
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.45, delay: 0.12 }}
        >
          <span className="h-4 w-[70%] self-start rounded-[8px] rounded-ss-[3px] bg-inbox-pill" />
          <span className="h-4 w-[82%] self-end rounded-[8px] rounded-se-[3px] bg-message-outbound" />
          <span className="h-4 w-[48%] self-start rounded-[8px] rounded-ss-[3px] bg-inbox-pill" />
        </motion.span>
      </div>
    </EmptyStateCopy>
  );
}

/** Compact list-pane state when the organization has no conversations. */
export function InboxListEmpty(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 overflow-hidden bg-inbox-rows px-6 py-8 text-center"
      data-testid="inbox-list-empty"
    >
      <span className="grid size-9 place-items-center rounded-full bg-brand-mint text-primary">
        <InboxIcon size={17} aria-hidden="true" />
      </span>
      <p className="m-0 text-[0.875rem] font-semibold text-inbox-strong">{t("inbox.emptyTitle")}</p>
      <p className="m-0 max-w-[15rem] text-[0.78125rem] leading-snug text-inbox-muted">
        {t("inbox.emptyDescription")}
      </p>
    </div>
  );
}

/** List-pane state when search or filters match nothing; the action uses the real clear handler. */
export function InboxNoResults({
  actionLabel,
  onAction,
}: {
  actionLabel: string;
  onAction: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 overflow-hidden bg-inbox-rows px-6 py-8 text-center"
      data-testid="inbox-list-no-results"
    >
      <span className="grid size-9 place-items-center rounded-full bg-inbox-pill text-inbox-subtle">
        <SearchXIcon size={17} aria-hidden="true" />
      </span>
      <p className="m-0 text-[0.875rem] font-semibold text-inbox-strong">{t("inbox.noResults.title")}</p>
      <p className="m-0 max-w-[15rem] text-[0.78125rem] leading-snug text-inbox-muted">
        {t("inbox.noResults.description")}
      </p>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 text-primary hover:bg-brand-mint"
        onClick={onAction}
      >
        {actionLabel}
      </Button>
    </div>
  );
}
